/**
 * Institution-vs-achievement discipline for the experience form (org faucet,
 * phase 2).
 *
 * The dancer experience form lets a user type a free-text name and, on no
 * search match, mint a brand-new `organizations` row. Dancers routinely type
 * ACHIEVEMENTS there — "First Place at OPES Dance Competition", "Jacqueline
 * Morland Awards", "Finalist" — which then pollute the org directory with rows
 * that are not institutions at all. `experiences.organization_manual` /
 * `organization_manual_type` free-text columns exist for exactly this; a typed
 * name that trips this classifier is routed there instead of minting an org.
 *
 * This is a lightweight, pattern-based (no i18n) heuristic run CLIENT-side at
 * create time. It is deliberately CONSERVATIVE — it favours false-negatives
 * (letting a real achievement slip through) over false-positives (blocking a
 * real organization name), because the user is always offered an override
 * ("it's an organization") when it trips. Real competition / institution names
 * that legitimately contain award-adjacent words must NOT match:
 *   - "Youth America Grand Prix" (bare "grand prix" is a competition name)
 *   - "Prix de Lausanne" (bare "prix" is a competition name)
 *   - "Royal Ballet School", "YAGP Barcelona"
 */

/**
 * Achievement / placement / award patterns. Each is anchored on a
 * word-boundary token that does not appear in ordinary institution names.
 *
 * Intentionally NOT matched (would flag real org / competition names):
 *   - bare "grand prix" / "prix" (Youth America Grand Prix, Prix de Lausanne)
 *   - bare ordinals ("1st", "2nd") without a place/prize/position noun
 */
const ACHIEVEMENT_PATTERNS: readonly RegExp[] = [
  // Ordinal placements: "First Place", "1st Prize", "2nd position", "3rd place".
  // Requires an explicit place/prize/position noun so "Prix de Lausanne" and
  // "Grand Prix" do not match.
  /\b(?:first|second|third|fourth|fifth|1st|2nd|3rd|4th|5th|\d+th)\s+(?:place|prize|position)\b/i,
  // Competition-result roles.
  /\b(?:finalist|semi[-\s]?finalist|winner|laureate)\b/i,
  // Award / prize / medal / scholarship nouns.
  /\b(?:awards?|prize|medal(?:l?ist)?|scholarship)\b/i,
  // "honorable mention" / "honourable mention".
  /\bhonou?rable\s+mention\b/i,
  // "Grand Prix winner" (the winner qualifier disambiguates from the
  // competition name "… Grand Prix").
  /\bgrand\s+prix\s+(?:winner|winners)\b/i,
];

/**
 * True when `name` looks like an achievement (award / placement / competition
 * result) rather than an organization/institution name. Conservative by
 * design — see the module docstring. Empty / whitespace-only input is `false`.
 */
export function isLikelyAchievement(name: string): boolean {
  const trimmed = name.trim();
  if (trimmed.length === 0) return false;
  return ACHIEVEMENT_PATTERNS.some((pattern) => pattern.test(trimmed));
}
